import { useState, useEffect } from 'react';
import { useTabNavigationStore } from '../../store/tabNavigationStore';
import { useAuthStore } from '../../store/authStore';
import { useInvite, validateInvite, getUserOrganizations } from '../../api/organizationApi';
import type { Organization } from '../../types/organization';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/ui/PageHeader';

export function JoinOrganizationScreen() {
  const { getCurrentScreen, popScreen } = useTabNavigationStore();
  const currentScreen = getCurrentScreen();

  const switchToOrganization = useAuthStore((state) => state.switchToOrganization);
  const user = useAuthStore((state) => state.user);

  const [inviteCode, setInviteCode] = useState((currentScreen.params?.inviteCode as string) || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkInviteCode = async () => {
      if (!user?.id) return;
      const paramCode = currentScreen.params?.inviteCode;
      if (paramCode && typeof paramCode === 'string') {
        const code = paramCode.trim().toUpperCase();
        setInviteCode(code);

        const codePattern = /^ORG-[A-Z0-9]{8}-[A-Z0-9]{8}$/;
        if (!codePattern.test(code)) {
          setError('Неверный формат кода приглашения');
          return;
        }

        try {
          setLoading(true);
          const inviteInfo = await validateInvite(code);
          const userOrgs = await getUserOrganizations();
          const isAlreadyMember = userOrgs.some(
            (org: Organization) => org.id === inviteInfo.organization_id
          );

          if (isAlreadyMember) {
            setError('Вы уже являетесь членом этой организации');
            setTimeout(() => {
              switchToOrganization(inviteInfo.organization_id);
              popScreen();
            }, 2000);
          } else {
            // In unified architecture, personal org is preserved — just join directly
            await confirmJoin();
          }
        } catch (err: any) {
          setError(err?.response?.data?.error || 'Не удалось проверить код приглашения');
        } finally {
          setLoading(false);
        }
      }
    };

    checkInviteCode();
  }, [currentScreen.params?.inviteCode, user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!inviteCode.trim()) {
      setError('Введите код приглашения');
      return;
    }

    const codePattern = /^ORG-[A-Z0-9]{8}-[A-Z0-9]{8}$/;
    if (!codePattern.test(inviteCode.trim().toUpperCase())) {
      setError('Неверный формат кода. Ожидается: ORG-XXXXXXXX-XXXXXXXX');
      return;
    }

    // In unified architecture, personal org is preserved — join directly without warning
    await confirmJoin();
  };

  const confirmJoin = async () => {
    try {
      setLoading(true);

      const result = await useInvite({ code: inviteCode.trim().toUpperCase() });
      await switchToOrganization(result.organization.id);
      popScreen();
    } catch (err: any) {
      if (err?.response?.status === 409 && err?.response?.data?.error === 'already a member') {
        setError('Вы уже являетесь членом этой организации');
        try {
          const inviteInfo = await validateInvite(inviteCode.trim().toUpperCase());
          setTimeout(() => {
            switchToOrganization(inviteInfo.organization_id);
            popScreen();
          }, 2000);
        } catch {
          // If validation fails, just show error
        }
      } else {
        setError(err?.response?.data?.error || err?.message || 'Не удалось подключиться к организации');
      }
    } finally {
      setLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px', borderRadius: '10px',
    border: '1px solid var(--separator)', backgroundColor: 'var(--bg-primary)',
    color: 'var(--text-primary)', fontSize: '17px', outline: 'none',
    fontFamily: 'monospace', textAlign: 'center', letterSpacing: '1px',
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><p style={{ color: 'var(--text-hint)' }}>Загрузка...</p></div>;
  }

  return (
    <div className="p-4 space-y-4 pb-24">
      <PageHeader
        title="Подключение к организации"
        subtitle="Введите код приглашения, который вы получили"
      />

      {error && (
        <Card>
          <p className="text-sm" style={{ color: 'var(--danger)' }}>{error}</p>
        </Card>
      )}

      {/* Invite Code Form */}
      <Card>
        <h3 className="text-[17px] font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Код приглашения</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            type="text"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
            placeholder="ORG-XXXXXXXX-XXXXXXXX"
            disabled={loading}
            maxLength={21}
            style={inputStyle}
          />
          <p className="text-xs text-center" style={{ color: 'var(--text-hint)' }}>Формат: ORG-XXXXXXXX-XXXXXXXX</p>
          <div className="flex gap-2">
            <Button onClick={() => {}} disabled={loading} fullWidth>
              Подключиться
            </Button>
            <Button onClick={() => popScreen()} variant="secondary" disabled={loading}>
              Назад
            </Button>
          </div>
        </form>
      </Card>

      {/* Info */}
      <Card>
        <p className="text-sm text-center" style={{ color: 'var(--text-hint)' }}>
          При вступлении в организацию вы будете переключены на неё. Ваша текущая организация сохранится — вы можете переключаться между ними в любое время.
        </p>
      </Card>
    </div>
  );
}
